/**
 * dd-all-collection.js – fixed price slider + fixed predictive search layout
 */

class DdAllCollection {
  constructor() {
    this.initDrawer();
    this.initSort();
    this.initQty();
    this.initPredictiveSearch();
    this.initAddToCart();
    this.initPriceSlider();
    this.initCheckboxes();
  }

  initDrawer() {
    const toggle = document.querySelector('[data-filter-toggle]');
    const sidebar = document.querySelector('[data-sidebar]');
    const overlay = document.querySelector('[data-sidebar-overlay]');
    if (!toggle || !sidebar) return;

    const open = () => {
      sidebar.classList.add('is-open');
      if (overlay) overlay.hidden = false;
      toggle.setAttribute('aria-expanded', 'true');
      document.body.style.overflow = 'hidden';
    };
    const close = () => {
      sidebar.classList.remove('is-open');
      if (overlay) overlay.hidden = true;
      toggle.setAttribute('aria-expanded', 'false');
      document.body.style.overflow = '';
    };

    toggle.addEventListener('click', () => sidebar.classList.contains('is-open') ? close() : open());
    overlay?.addEventListener('click', close);
  }

  initSort() {
    const select = document.querySelector('[data-sort-select]');
    if (!select) return;
    select.addEventListener('change', () => {
      const url = new URL(window.location.href);
      url.searchParams.set('sort_by', select.value);
      window.location.href = url.toString();
    });
  }

  initQty() {
    document.querySelectorAll('[data-dd-card-form]').forEach((form) => {
      const input = form.querySelector('.dd-product-card-shop__qty-input');
      const minus = form.querySelector('[data-qty-minus]');
      const plus = form.querySelector('[data-qty-plus]');
      if (!input) return;

      minus?.addEventListener('click', () => {
        input.value = Math.max(1, parseInt(input.value, 10) - 1);
      });
      plus?.addEventListener('click', () => {
        const max = parseInt(input.max, 10) || 99;
        input.value = Math.min(max, parseInt(input.value, 10) + 1);
      });
    });
  }

  /* ===== FIXED PREDICTIVE SEARCH (no more squished/wrapped layout) ===== */
  initPredictiveSearch() {
    const input = document.querySelector('[data-dd-predictive-search]');
    const results = document.querySelector('[data-predictive-results]');
    if (!input || !results) return;

    let timer;
    input.addEventListener('input', () => {
      clearTimeout(timer);
      const q = input.value.trim();
      if (q.length < 2) {
        results.hidden = true;
        results.innerHTML = '';
        return;
      }

      timer = setTimeout(async () => {
        try {
          const res = await fetch(`/search/suggest.json?q=${encodeURIComponent(q)}&resources[type]=product&resources[limit]=6&resources[options][fields]=title,product_type,vendor`);
          const data = await res.json();
          const products = data.resources?.results?.products || [];

          if (products.length === 0) {
            results.innerHTML = `<p class="dd-all-collection__predictive-empty">No results</p>`;
          } else {
            results.innerHTML = products.map((p) => `
              <a href="${p.url}" class="dd-all-collection__predictive-item">
                <span class="dd-all-collection__predictive-thumb">
                  <img src="${p.image || ''}" alt="" loading="lazy">
                </span>
                <span class="dd-all-collection__predictive-info">
                  <span class="dd-all-collection__predictive-title">${p.title}</span>
                  ${p.price ? `<span class="dd-all-collection__predictive-price">${p.price}</span>` : ''}
                </span>
              </a>
            `).join('');
          }
          results.hidden = false;
        } catch (e) {
          results.hidden = true;
        }
      }, 280);
    });

    document.addEventListener('click', (e) => {
      if (!e.target.closest('.dd-all-collection__search-wrap')) {
        results.hidden = true;
      }
    });
  }

  initAddToCart() {
    document.querySelectorAll('[data-dd-card-form]').forEach((form) => {
      form.addEventListener('submit', async (e) => {
        e.preventDefault();
        const btn = form.querySelector('[data-dd-atc]');
        if (!btn) return;

        const originalText = btn.textContent;
        btn.setAttribute('aria-busy', 'true');
        btn.textContent = 'Adding…';

        try {
          const formData = new FormData(form);

          // 1. Add item
          const addRes = await fetch((window.Shopify?.routes?.root || '/') + 'cart/add.js', {
            method: 'POST',
            body: formData,
            headers: { 'Accept': 'application/json' }
          });
          if (!addRes.ok) throw new Error('Add failed');

          // 2. Re-render cart drawer
          const sectionRes = await fetch(`${window.Shopify.routes.root}?sections=cart-drawer-section`);
          const sectionData = await sectionRes.json();
          const sectionEl = document.getElementById('shopify-section-cart-drawer-section');
          if (sectionEl && sectionData['cart-drawer-section']) {
            sectionEl.innerHTML = sectionData['cart-drawer-section'];
          }

          // 3. Get fresh cart
          const cartRes = await fetch((window.Shopify?.routes?.root || '/') + 'cart.js');
          const cart = await cartRes.json();

          // 4. Update header cart bubble (and un-hide it)
          const updateBubble = () => {
            // Update the number
            document.querySelectorAll(
              '.cart-bubble__text-count, [ref="cartBubbleCount"], [data-testid="cart-bubble"]'
            ).forEach((el) => {
              el.textContent = cart.item_count;
              el.classList.remove('hidden');           // ← remove hidden
              el.removeAttribute('aria-hidden');
            });

            // Un-hide the bubble itself
            document.querySelectorAll('.cart-bubble').forEach((bubble) => {
              if (cart.item_count > 0) {
                bubble.classList.remove('visually-hidden', 'hidden', 'hide');
                bubble.style.display = '';
                bubble.removeAttribute('hidden');
              }
            });
          };

          updateBubble();
          setTimeout(updateBubble, 100);
          setTimeout(updateBubble, 300);

          // 5. Open the cart drawer
          setTimeout(() => {
            const cartBtn = document.querySelector('.dd-header__icon-btn--cart, [aria-controls="cart-drawer"]');
            if (cartBtn) cartBtn.click();
          }, 60);

          btn.textContent = 'Added ✓';
          setTimeout(() => {
            btn.textContent = originalText;
            btn.removeAttribute('aria-busy');
          }, 1500);

        } catch (err) {
          console.error(err);
          btn.textContent = 'Error';
          setTimeout(() => {
            btn.textContent = originalText;
            btn.removeAttribute('aria-busy');
          }, 1500);
        }
      });
    });
  }
  
  /* ===== PRICE SLIDER (single defensive unit-conversion point, fully clamped) ===== */
  initPriceSlider() {
    const slider = document.querySelector('[data-price-slider]');
    if (!slider) return;

    const minHandle = slider.querySelector('[data-min-handle]');
    const maxHandle = slider.querySelector('[data-max-handle]');
    const rangeEl   = slider.querySelector('[data-range]');
    const minDisplay = slider.querySelector('[data-min-display]');
    const maxDisplay = slider.querySelector('[data-max-display]');
    const applyBtn  = slider.querySelector('[data-price-apply]');
    const paramMin = slider.dataset.paramMin;
    const paramMax = slider.dataset.paramMax;

    const clamp = (val, lo, hi) => Math.min(Math.max(val, lo), hi);

    // Bounds are always in cents and never change for this collection.
    const absMin = Number(slider.dataset.rangeMinCents) || 0;
    let absMax = Number(slider.dataset.rangeMaxCents) || (absMin + 10000);
    if (absMax <= absMin) absMax = absMin + 10000; // guard against a degenerate range

    // Current selection: read straight from the URL's own query params —
    // this is the exact value we wrote on Apply, so it's the most reliable
    // source (Shopify's filter.min_value/max_value echo wasn't always current).
    const urlParams = new URLSearchParams(window.location.search);
    const parseDollarsToCents = (raw) => {
      const n = parseFloat(raw);
      return Number.isFinite(n) ? Math.round(n * 100) : null;
    };

    let currentMin = parseDollarsToCents(urlParams.get(paramMin));
    let currentMax = parseDollarsToCents(urlParams.get(paramMax));
    if (currentMin === null) currentMin = absMin;
    if (currentMax === null) currentMax = absMax;

    // Defensive clamp — protects against stale/out-of-range values sitting in the URL.
    currentMin = clamp(currentMin, absMin, absMax);
    currentMax = clamp(currentMax, absMin, absMax);
    if (currentMin > currentMax) [currentMin, currentMax] = [currentMax, currentMin];

    const minGap = Math.max(100, Math.round((absMax - absMin) * 0.01)); // at least $1 or 1% of range

    const toPercent = (val) => clamp(((val - absMin) / (absMax - absMin)) * 1000, 0, 1000);
    const fromPercent = (pct) => absMin + (clamp(Number(pct), 0, 1000) / 1000) * (absMax - absMin);

    const formatMoney = (cents) => {
      const amount = (Number(cents) || 0) / 100;
      return new Intl.NumberFormat(undefined, {
        style: 'currency',
        currency: (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'USD',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0
      }).format(amount);
    };

    const updateUI = () => {
      const minPct = toPercent(currentMin);
      const maxPct = toPercent(currentMax);

      minHandle.value = minPct;
      maxHandle.value = maxPct;

      const left = clamp(minPct / 10, 0, 100);
      const width = clamp((maxPct - minPct) / 10, 0, 100 - left);

      rangeEl.style.left  = left + '%';
      rangeEl.style.width = width + '%';

      minDisplay.textContent = formatMoney(currentMin);
      maxDisplay.textContent = formatMoney(currentMax);
    };

    minHandle.addEventListener('input', () => {
      const val = fromPercent(minHandle.value);
      currentMin = clamp(val, absMin, currentMax - minGap);
      updateUI();
    });

    maxHandle.addEventListener('input', () => {
      const val = fromPercent(maxHandle.value);
      currentMax = clamp(val, currentMin + minGap, absMax);
      updateUI();
    });

    applyBtn?.addEventListener('click', () => {
      const url = new URL(window.location.href);
      // Shopify's price filter params expect major-unit decimals (e.g. "25.00").
      url.searchParams.set(paramMin, (currentMin / 100).toFixed(2));
      url.searchParams.set(paramMax, (currentMax / 100).toFixed(2));
      window.location.href = url.toString();
    });

    updateUI();
  }

  initCheckboxes() {
    document.querySelectorAll('.dd-all-collection__checkbox').forEach((cb) => {
      cb.addEventListener('change', () => {
        const url = cb.checked ? cb.dataset.filterUrlAdd : cb.dataset.filterUrlRemove;
        if (url) window.location.href = url;
      });
    });
  }
}

document.addEventListener('DOMContentLoaded', () => new DdAllCollection());